import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({ 
  label,
  error,
  helperText,
  showPasswordToggle = false,
  type = 'text',
  className = '',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasValueRef = useRef(false);
  
  // Combine forwarded ref with internal ref
  const combinedRef = (element: HTMLInputElement) => {
    inputRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };
  
  // Check for initial value and when props.value changes
  useEffect(() => {
    const checkValue = () => {
      if (inputRef.current) {
        const currentValue = inputRef.current.value;
        setHasValue(Boolean(currentValue && currentValue.trim()));
      }
    };

    // Check immediately
    checkValue();

    // Also check after a small delay to catch React Hook Form setValue updates
    const timer = setTimeout(checkValue, 10);

    return () => clearTimeout(timer);
  }, [props.value, props.defaultValue]);

  // Keep ref in sync so interval closure always has the latest value
  hasValueRef.current = hasValue;

  // Periodically check for programmatic value changes (e.g. React Hook Form setValue)
  // Dep array is [] so only ONE interval per mounted Input, never recreated
  useEffect(() => {
    const checkValue = () => {
      if (inputRef.current) {
        const currentValue = inputRef.current.value;
        const hasCurrentValue = Boolean(currentValue && currentValue.trim());
        if (hasCurrentValue !== hasValueRef.current) {
          setHasValue(hasCurrentValue);
        }
      }
    };

    const interval = setInterval(checkValue, 100);
    return () => clearInterval(interval);
  }, []);
  
  // Separate out event handlers from other props
  const { onFocus, onBlur, onChange, ...otherProps } = props;

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setHasValue(Boolean(e.target.value && e.target.value.trim()));
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(Boolean(e.target.value && e.target.value.trim()));
    onChange?.(e);
  };

  const inputType = showPasswordToggle && showPassword ? 'text' : type;
  const isLabelFloating = isFocused || hasValue;

  return (
    <div className="w-full">
      <div className="relative">
        <input
          ref={combinedRef}
          type={inputType}
          className={`
            peer w-full px-3 py-2.5 border border-gray-300 rounded-lg 
            focus:outline-none focus:ring-0 focus:border-blue-500
            transition-colors duration-200
            placeholder:text-transparent
            ${error ? 'border-red-300 focus:border-red-500' : 'hover:border-gray-400'}
            ${showPasswordToggle ? 'pr-10' : ''}
            ${className}
          `}
          placeholder=" "
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          {...otherProps}
        />
        
        {label && (
          <label
            className={`
              absolute left-3 transition-[top,font-size,color] duration-200 pointer-events-none
              ${isLabelFloating
                ? '-top-2 text-xs text-gray-600 bg-white px-2'
                : 'top-1/2 -translate-y-1/2 text-sm text-gray-400'
              }
              ${error && isLabelFloating ? 'text-red-500' : ''}
              ${isFocused && !error ? 'text-blue-500' : ''}
            `}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        {showPasswordToggle && (
          <button
            type="button"
            onClick={handlePasswordToggle}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        )}
      </div>
      
      {(error || helperText) && (
        <div className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;